<template>
  <div class="home general" v-on:scroll="onScroll">
    <h1 class="section-title">
      Canciones reportadas
    </h1>

    <va-data-table
      class="ekitable"
      :items="songs"
      :columns="columns"
      no-data-html="<div class='no-data'>Sin canciones con reportes abiertos</div>"
      :sticky-header="true"
      :loading="false"
      loading-text=""
    >
      <template #headerPrepend>
        <tr>
          <th colspan="7">
            <va-input
              class="search-bar"
              title="Buscar canción reportada"
              v-title
              placeholder="Número, título o artista"
              v-on:keyup.enter="onEnter"
              v-on:focusout="onEnter"
              v-model="search"
            >
              <template #prependInner>
                <font-awesome-icon :icon="['fa-solid', 'search']" />
              </template>
            </va-input>
          </th>
        </tr>
      </template>
      <template #cell(lastReportAt)="{ rowData }">
        {{ formatDate(rowData.lastReportAt) }}
      </template>
      <template #cell(actions)="{ rowData }">
        <va-button color="info" title="Ver reportes" v-title @click="openReports(rowData)">
          Ver
        </va-button>
      </template>
    </va-data-table>

    <VaModal
      v-model="showReports"
      hide-default-actions
      size="large"
      :title="modalTitle"
    >
      <div v-if="loadingReports" class="report-empty">Cargando reportes...</div>
      <div v-else-if="reports.length === 0" class="report-empty">Esta canción no tiene reportes.</div>
      <div v-else class="report-list">
        <div v-for="report in reports" :key="report.id" class="report-item">
          <div class="report-meta">
            <strong>{{ report.userName || 'Usuario' }}</strong>
            <span v-if="report.userEmail">{{ report.userEmail }}</span>
            <span>{{ formatDate(report.createdAt) }}</span>
          </div>
          <p class="report-text">{{ report.description }}</p>
          <div v-if="report.device" class="report-device">
            <div v-for="(line, index) in deviceLines(report.device)" :key="index">{{ line }}</div>
            <div v-if="report.device.userAgent" class="report-ua">{{ report.device.userAgent }}</div>
          </div>
          <va-button
            v-if="!report.resolved"
            color="success"
            :disabled="report.resolving"
            @click="askResolution(report)"
          >
            Marcar como resuelto
          </va-button>
          <div v-else>
            <div class="report-done">
              Resuelto<span v-if="report.resolvedAt"> el {{ formatDate(report.resolvedAt) }}</span>
            </div>
            <p v-if="report.resolution" class="report-text">{{ report.resolution }}</p>
          </div>
        </div>
      </div>
      <div class="report-close">
        <va-button color="secondary" @click="showReports = false">Cerrar</va-button>
      </div>
    </VaModal>

    <VaModal
      v-model="showResolution"
      ok-text="Marcar como resuelto"
      cancel-text="Cancelar"
      size="small"
      :child:okButton="{ disabled: !resolutionDetail.trim() || resolving }"
      @ok="confirmResolve"
    >
      <h3 class="va-h3">Solución del reporte</h3>
      <p>Describe qué se hizo para solucionar el problema. Este dato es obligatorio.</p>
      <va-input
        v-model="resolutionDetail"
        type="textarea"
        placeholder="Qué se hizo para solucionar el problema"
        style="width: 100%;"
      />
    </VaModal>
  </div>
</template>

<script>
import requester from "@/services/requester";

export default {
  data() {
    return {
      page: 1,
      isLoading: false,
      songs: [],
      continueLoading: true,
      search: "",
      showReports: false,
      loadingReports: false,
      reports: [],
      selectedSong: null,
      showResolution: false,
      resolutionDetail: "",
      reportToResolve: null,
      resolving: false,
      columns: [
        { key: "num", sortable: false, label: "Número" },
        { key: "title", sortable: false, label: "Canción" },
        { key: "artist", sortable: false, label: "Artista" },
        { key: "openCount", sortable: false, label: "Abiertos" },
        { key: "reportCount", sortable: false, label: "Total" },
        { key: "lastReportAt", sortable: false, label: "Último reporte" },
        { key: "actions", sortable: false, label: "" },
      ],
    };
  },
  computed: {
    modalTitle() {
      if (!this.selectedSong) {
        return "Reportes";
      }
      const artist = this.selectedSong.artist ? " — " + this.selectedSong.artist : "";
      return this.selectedSong.num + " " + this.selectedSong.title + artist;
    },
  },
  methods: {
    deviceLines(device) {
      const dash = (value) => (value === 0 || value ? value : "—");
      const yesNo = (value) => (value ? "sí" : "no");
      const connection = device.connection || {};
      const screen = device.screen || {};
      const viewport = device.viewport || {};
      const howler = device.howler || {};
      const lines = [
        "Browser: " + dash(device.browser),
        "Cliente: " + (device.client === "tauri" ? "Escritorio" : "Web"),
        "Memoria: " + (device.deviceMemory != null ? device.deviceMemory + " GB" : "—"),
        "Núcleos: " + dash(device.hardwareConcurrency),
        "Pantalla: " + dash(screen.width) + "x" + dash(screen.height) +
          "  viewport " + dash(viewport.width) + "x" + dash(viewport.height),
        "Red: " + (device.onLine === false ? "sin conexión" : "en línea") +
          "  tipo=" + dash(connection.effectiveType || connection.type) +
          "  rtt=" + dash(connection.rtt) +
          "  downlink=" + dash(connection.downlink) +
          "  saveData=" + yesNo(connection.saveData),
        "Bajo recurso: " + yesNo(device.isLowResource) +
          "  bajo hardware: " + yesNo(device.isLowHardware) +
          "  Tizen 5: " + yesNo(device.isLegacyTizen),
      ];
      if (device.howler) {
        lines.push(
          "Audio: webAudio=" + yesNo(howler.usingWebAudio) +
            "  noAudio=" + yesNo(howler.noAudio) +
            "  ctx=" + dash(howler.ctxState) +
            "  sampleRate=" + dash(howler.ctxSampleRate)
        );
      }
      lines.push("IP: " + dash(device.ip));
      return lines;
    },
    formatDate(value) {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleString("es-EC", { timeZone: "America/Guayaquil" });
    },
    onEnter() {
      this.page = 1;
      this.songs = [];
      this.continueLoading = true;
      this.getFromApi();
    },
    async getFromApi(isScroll = false) {
      this.isLoading = true;
      const data = await requester.Get(
        `/reported-songs/${this.page}/?search=${encodeURIComponent(this.search)}`,
        true
      );
      if (data.status === 200) {
        const pageItems = data.data.songs || [];
        if (!isScroll) {
          this.songs = pageItems;
        } else {
          this.songs = this.songs.concat(pageItems);
        }
        if (pageItems.length > 0) {
          this.page++;
          this.continueLoading = true;
        } else {
          this.continueLoading = false;
        }
      }
      this.isLoading = false;
    },
    onScroll(event) {
      if (!this.continueLoading || this.isLoading) {
        return;
      }
      const { target } = event;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
        this.getFromApi(true);
      }
    },
    async openReports(song) {
      this.selectedSong = song;
      this.reports = [];
      this.showReports = true;
      this.loadingReports = true;
      try {
        const data = await requester.Get(`/song-reports/${song.id}`, true);
        if (data.status === 200) {
          this.reports = data.data.reports || [];
        }
      } catch (error) {
        this.reports = [];
      }
      this.loadingReports = false;
    },
    askResolution(report) {
      if (report.resolved || this.resolving) {
        return;
      }
      this.reportToResolve = report;
      this.resolutionDetail = "";
      this.showResolution = true;
    },
    async confirmResolve() {
      const report = this.reportToResolve;
      const resolution = (this.resolutionDetail || "").trim();
      if (!report || !resolution || this.resolving) {
        this.$nextTick(() => {
          this.showResolution = true;
        });
        return;
      }
      this.resolving = true;
      report.resolving = true;
      try {
        const data = await requester.Post(`/resolve-song-report/${report.id}`, true, {
          resolution: resolution.slice(0, 2000),
        });
        if (data.status === 200) {
          report.resolved = true;
          report.resolvedAt = data.data.resolvedAt;
          report.resolution = data.data.resolution;
          const listed = this.songs.find((item) => item.id === (this.selectedSong && this.selectedSong.id));
          if (listed && listed.openCount > 0) {
            listed.openCount -= 1;
          }
          if (listed && listed.openCount <= 0) {
            this.songs = this.songs.filter((item) => item.id !== listed.id);
            this.showReports = false;
          }
          this.resolutionDetail = "";
          this.reportToResolve = null;
        } else {
          this.$nextTick(() => {
            this.showResolution = true;
          });
        }
      } catch (error) {
        this.$nextTick(() => {
          this.showResolution = true;
        });
      }
      report.resolving = false;
      this.resolving = false;
    },
  },
  created() {
    this.onEnter();
  },
};
</script>

<style lang="scss" scoped>
.report-list {
  max-height: 60vh;
  overflow: auto;
}

.report-item {
  padding: 12px 0;
  border-bottom: 1px solid var(--va-background-border, #e5e5e5);
}

.report-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 6px;
}

.report-text {
  white-space: pre-wrap;
  margin: 0 0 10px;
}

.report-device {
  margin: 0 0 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
  font-size: 13px;
  line-height: 1.45;
}

.report-ua {
  margin-top: 4px;
  opacity: 0.7;
  word-break: break-word;
}

.report-done {
  color: var(--va-success, #2e7d32);
}

.report-empty,
.report-close {
  margin-top: 12px;
}
</style>
